import copy from 'copy-to-clipboard';
import { createContext, useCallback, useContext } from 'react';

export type CopyTextCallbacks = {
  onCopySuccess?: (text: string) => void;
  onCopyError?: (text: string) => void;
};

const CopyTextContext = createContext<CopyTextCallbacks>({});

/**
 * Lets the consuming app decide how copy results are announced (e.g. toasts)
 * without the component library depending on the app's notification system.
 */
export function CopyTextProvider({
  children,
  onCopySuccess,
  onCopyError,
}: CopyTextCallbacks & { children: React.ReactNode }) {
  return (
    <CopyTextContext.Provider value={{ onCopySuccess, onCopyError }}>
      {children}
    </CopyTextContext.Provider>
  );
}

/** Returns a function that copies text and reports whether it succeeded. */
export function useCopyText() {
  const { onCopySuccess, onCopyError } = useContext(CopyTextContext);

  return useCallback(
    (text: string) => {
      const result = copy(text);
      if (result) {
        onCopySuccess?.(text);
      } else {
        onCopyError?.(text);
      }
      return result;
    },
    [onCopySuccess, onCopyError]
  );
}
